import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { ApiError, apiClient } from '../api/client'
import {
  ADMIN_MERGE_REQUEST_CONFIG,
  ADMIN_ROOT_PATH,
  ADMIN_SECTION_CONFIG,
  adminSectionPath,
  type EditionFeatureKey,
} from './admin'
import { AUTH_ROUTE_NAME, authLoginLocation, roleAllows, type ProtectedRole } from './auth'

const adminMeta = { surface: 'admin', requiresAuth: true, role: 'ADMIN' } as const
const adminSectionRoutes: RouteRecordRaw[] = Object.values(ADMIN_SECTION_CONFIG).map((section) => ({
  path: section.path,
  name: section.routeName,
  component: section.key === 'rendering'
    ? () => import('../views/AdminRenderingView.vue')
    : section.key === 'overview'
      ? () => import('../views/AdminOverviewView.vue')
    : () => import('../views/AdminView.vue'),
  // Only team-owned sections carry an edition feature; keep the route type
  // narrow for the standard sections.
  meta: {
    ...adminMeta,
    adminSection: section.key,
    ...('editionFeature' in section && section.editionFeature
      ? { editionFeature: section.editionFeature } : {}),
  },
}))

const adminRoute: RouteRecordRaw = {
  path: ADMIN_ROOT_PATH,
  name: 'admin',
  component: () => import('../layouts/AdminLayout.vue'),
  redirect: () => ({ path: adminSectionPath('overview'), query: {} }),
  meta: adminMeta,
  children: [
    ...adminSectionRoutes,
    {
      path: ADMIN_MERGE_REQUEST_CONFIG.path,
      name: ADMIN_MERGE_REQUEST_CONFIG.routeName,
      component: () => import('../views/AdminMergeRequestsView.vue'),
      meta: { ...adminMeta, adminSection: ADMIN_MERGE_REQUEST_CONFIG.key, editionFeature: 'mergeRequests' },
    },
    {
      path: `${ADMIN_MERGE_REQUEST_CONFIG.path}/:mergeRequestId`,
      name: ADMIN_MERGE_REQUEST_CONFIG.detailRouteName,
      component: () => import('../views/AdminMergeRequestDetailView.vue'),
      meta: { ...adminMeta, adminSection: ADMIN_MERGE_REQUEST_CONFIG.key, editionFeature: 'mergeRequests' },
    },
    { path: ':pathMatch(.*)*', redirect: () => ({ path: adminSectionPath('overview'), query: {} }) },
  ],
}

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', name: 'public-map', component: () => import('../views/PublicMapView.vue'), meta: { surface: 'public' } },
    { path: '/survey', name: 'survey-console', component: () => import('../views/SurveyConsoleView.vue'), meta: { surface: 'survey', requiresAuth: true, role: 'SURVEYOR' } },
    { path: '/survey/:pathMatch(.*)*', name: 'survey-console-subroute', component: () => import('../views/SurveyConsoleView.vue'), meta: { surface: 'survey', requiresAuth: true, role: 'SURVEYOR' } },
    { path: '/login', name: AUTH_ROUTE_NAME, component: () => import('../views/AuthView.vue'), meta: { surface: 'auth' } },
    adminRoute,
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
})

const isAuthenticationError = (error: unknown) => error instanceof ApiError
  && (error.status === 401 || error.status === 403 || error.code === 'AUTHENTICATION_REQUIRED' || error.code === 'AUTHORIZATION_REQUIRED')

router.beforeEach(async (to) => {
  const editionFeature = typeof to.meta.editionFeature === 'string'
    ? to.meta.editionFeature as EditionFeatureKey : undefined
  if (editionFeature) {
    try {
      const capabilities = await apiClient.capabilities
      if (!capabilities.features || capabilities.features[editionFeature] !== true) {
        return { name: ADMIN_SECTION_CONFIG.overview.routeName }
      }
    } catch (error) {
      // A real but unavailable capabilities endpoint is treated as standard;
      // a network-offline browser keeps the existing team demo experience.
      if (!(error instanceof ApiError && error.status === 0)) {
        return { name: ADMIN_SECTION_CONFIG.overview.routeName }
      }
    }
  }
  if (!to.meta.requiresAuth || to.name === AUTH_ROUTE_NAME) return true

  const requiredRole = to.meta.role === 'ADMIN' || to.meta.role === 'SURVEYOR'
    ? to.meta.role as ProtectedRole
    : undefined
  try {
    const context = await apiClient.me()
    return roleAllows(context.role, requiredRole)
      ? true
      : authLoginLocation(to.fullPath, requiredRole, 'forbidden')
  } catch (error) {
    // Keep the existing API-unavailable demo behavior. Only an explicit
    // authentication failure should move the user to the login page.
    return isAuthenticationError(error) ? authLoginLocation(to.fullPath, requiredRole) : true
  }
})
